import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useMemo } from "react";
import { useYupValidationResolver } from "../../../../hooks/useYupValidationResolver";
import { SIMPLE_TYPE } from "../constants";
import { getDialCode, getNumberWithoutDialCode, startsWithLetters } from "../../../../constants/countryCodes";

const validationSchema = yup.object({
    title: yup.string(),
    forename: yup.string().required("Field is required"),
    surname: yup.string().required("Field is required"),
    suffix: yup.string(),
    relationshipTypeId: yup.string().required("Field is required"),
    dateOfBirth: yup.string().required("Field is required"),
    contactNumberCountryCode: yup.object().shape({ id: yup.string(), label: yup.string() }).test(
        "not-empty",
        "Field is required",
        (value) => value && Object.keys(value).length > 0,
    ),
    contactNumber: yup.string().required("Field is required"),
    emailAddress: yup.string().emailRegex("Email address is invalid").required("Field is required"),
    emailAddressConfirm: yup
        .string()
        .oneOf([yup.ref("emailAddress")], "Email addresses do not match")
        .required("Field is required"),
    addressLine1: yup.string().required("Field is required"),
    addressLine2: yup.string(),
    city: yup.string().required("Field is required"),
    code: yup.string().required("Field is required"),
    area: yup.string(),
    country: yup.string().required("Field is required"),
    contactType: yup.string(),
    hasParentHubAccess: yup.bool(),
});

const emptyFields = {
    title: "",
    forename: "",
    surname: "",
    suffix: "",
    relationshipTypeId: "",
    dateOfBirth: "",
    contactNumberCountryCode: undefined,
    contactNumber: "",
    emailAddress: "",
    emailAddressConfirm: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    code: "",
    area: "",
    country: "",
    contactType: SIMPLE_TYPE,
    hasParentHubAccess: false,
    allowRenewals: false,
    allowFinance: false,
};

export const useContactForm = (data) => {
    const defaultValues = useMemo(() => {
        const dataWithoutNull = Object.fromEntries(Object.entries(data || {})
            .filter(([key]) => key in emptyFields)
            .filter(([, value]) => value !== null));
        const addressWithoutNull = Object.fromEntries(Object.entries(data?.primaryAddress || {})
            .filter(([key]) => key in emptyFields)
            .filter(([, value]) => value !== null));

        const phoneCountryCode = data?.primaryPhone?.countryCode ?? getDialCode(data?.primaryPhone?.number);
        const phoneNumber = startsWithLetters(data?.primaryPhone?.number) ? getNumberWithoutDialCode(data?.primaryPhone?.number) : data?.primaryPhone?.number;

        return {
            ...emptyFields,
            ...addressWithoutNull,
            ...dataWithoutNull,
            emailAddressConfirm: data?.emailAddress,
            contactNumberCountryCode: phoneCountryCode ? { id: phoneCountryCode } : emptyFields.contactNumberCountryCode,
            contactNumber: phoneNumber || emptyFields.contactNumber,
        };
    }, [data]);
    const resolver = useYupValidationResolver(validationSchema);

    const {
        control,
        handleSubmit,
        register,
        reset,
        formState: { errors, isDirty },
        watch,
    } = useForm({
        resolver,
        defaultValues,
        mode: "onChange",
    });

    const isDirtyForm = isDirty;

    return {
        control,
        register,
        handleSubmit,
        reset,
        errors,
        isDirtyForm,
        watch,
    };
};
